/**
 * Line Chart Widget Component
 * Displays data as a line chart using Recharts
 */

import { useMemo } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { ChartData, ChartConfig } from '../../types';

interface ChartLineWidgetProps {
  data: ChartData;
  config?: ChartConfig;
  loading?: boolean;
  title?: string;
}

const DEFAULT_COLORS = [
  '#3b82f6', // blue
  '#22c55e', // green
  '#eab308', // yellow
  '#ef4444', // red
  '#8b5cf6', // purple
  '#ec4899', // pink
  '#14b8a6', // teal
  '#f97316', // orange
];

export function ChartLineWidget({
  data,
  config,
  loading,
  title,
}: ChartLineWidgetProps) {
  // Transform data if needed
  const chartData = useMemo(() => {
    if (!data.datasets || data.datasets.length === 0) {
      return [];
    }

    // If data is already in the right format
    if (data.labels && data.datasets[0].data) {
      return data.labels.map((label, idx) => {
        const point: Record<string, unknown> = { name: label };
        data.datasets.forEach((dataset, datasetIdx) => {
          point[dataset.label || `series${datasetIdx}`] = dataset.data[idx];
        });
        return point;
      });
    }

    return data.datasets[0].data as Record<string, unknown>[];
  }, [data]);

  // Get series keys (excluding 'name')
  const seriesKeys = useMemo(() => {
    if (chartData.length === 0) return [];
    return Object.keys(chartData[0]).filter((key) => key !== 'name');
  }, [chartData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full p-4">
        <div className="animate-pulse w-full h-full bg-gray-100 rounded" />
      </div>
    );
  }

  if (chartData.length === 0) {
    return (
      <div className="flex items-center justify-center h-full p-8 text-gray-500">
        <p>No data available</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full p-4">
      {title && (
        <h3 className="text-sm font-medium text-gray-700 mb-2">{title}</h3>
      )}
      <div className="flex-1 min-h-0">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart
            data={chartData}
            margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis
              dataKey="name"
              tick={{ fontSize: 12, fill: '#6b7280' }}
              tickLine={{ stroke: '#d1d5db' }}
              axisLine={{ stroke: '#d1d5db' }}
            />
            <YAxis
              tick={{ fontSize: 12, fill: '#6b7280' }}
              tickLine={{ stroke: '#d1d5db' }}
              axisLine={{ stroke: '#d1d5db' }}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: '#fff',
                border: '1px solid #e5e7eb',
                borderRadius: '6px',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              }}
            />
            {seriesKeys.length > 1 && <Legend />}
            {seriesKeys.map((key, idx) => (
              <Line
                key={key}
                type="monotone"
                dataKey={key}
                stroke={config?.colors?.[idx] || DEFAULT_COLORS[idx % DEFAULT_COLORS.length]}
                strokeWidth={2}
                dot={{ r: 4 }}
                activeDot={{ r: 6 }}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
